import { useState, useEffect, useRef } from 'react';
import MeService from '../services/MeService';
import ImpersonationService from '../services/ImpersonationService';

/**
 * Hook to check if the current user has the Imperator role
 * (When on-behalf-of is in use, this reflects the *effective* user, not the logged-in user.)
 * @returns {boolean | null} - true if Imperator, false if not, null if not yet loaded
 */
export const useIsImperator = (authResolved?: boolean): boolean | null => {
    const [isImperator, setIsImperator] = useState<boolean | null>(null);

    useEffect(() => {
        const checkImperator = () => {
            const apiRole = MeService.getApiRole();
            if (apiRole !== null) {
                setIsImperator(apiRole === 'Imperator');
            } else {
                setIsImperator(null);
            }
        };

        // Check immediately
        checkImperator();

        // Subscribe to changes
        const unsubscribe = MeService.subscribe(checkImperator);

        // Only fetch if auth is resolved and Me data is not already loaded
        // Don't fetch if authResolved is explicitly false (auth not ready)
        // The App component handles fetching Me data when auth is resolved,
        // so we only fetch here if auth is resolved but Me data hasn't been fetched yet
        if (authResolved === true && MeService.getMe() === null && !MeService.isLoading()) {
            MeService.fetchMe().catch(() => {
                // Error handling is done in MeService
            });
        }

        return unsubscribe;
    }, [authResolved]);

    return isImperator;
};

/**
 * Cached imperator status of the actual logged-in user (ignoring x-on-behalf-of).
 * Updated only when on-behalf-of is disabled so UI that must respect the real user (e.g. Footer checkbox) can use this.
 */
let cachedActualUserIsImperator: boolean | null = null;

function updateActualUserImperatorCache(): void {
    if (!ImpersonationService.getOnBehalfOfEnabled() || ImpersonationService.getOnBehalfOfUpn().trim() === '') {
        const apiRole = MeService.getApiRole();
        cachedActualUserIsImperator = apiRole !== null ? apiRole === 'Imperator' : null;
    }
}

/**
 * Hook that returns whether the *actual* logged-in user is an Imperator (not the effective user when on-behalf-of is in use).
 * Use this for UI that must stay visible to the real user, e.g. the "On behalf of" checkbox in the Footer.
 * When on-behalf-of is disabled, the cache is updated from entities/me. When on-behalf-of is enabled, the last known actual-user status is returned.
 * @returns {boolean | null} - true if actual user is Imperator, false if not, null if unknown (e.g. page loaded with on-behalf-of already on)
 */
export const useActualUserIsImperator = (authResolved?: boolean): boolean | null => {
    const [actualUserIsImperator, setActualUserIsImperator] = useState<boolean | null>(() => {
        updateActualUserImperatorCache();
        return cachedActualUserIsImperator;
    });
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        const sync = () => {
            updateActualUserImperatorCache();
            if (mountedRef.current) setActualUserIsImperator(cachedActualUserIsImperator);
        };
        sync();
        const unsubMe = MeService.subscribe(sync);
        const unsubObo = ImpersonationService.subscribeOnBehalfOf(sync);
        if (authResolved === true && MeService.getMe() === null && !MeService.isLoading()) {
            MeService.fetchMe().catch(() => {});
        }
        return () => {
            mountedRef.current = false;
            unsubMe();
            unsubObo();
        };
    }, [authResolved]);

    return actualUserIsImperator;
};

/**
 * Hook to get the current user's ApiRole
 * @returns {string | null} - The ApiRole string, or null if not yet loaded
 */
export const useApiRole = (): string | null => {
    const [apiRole, setApiRole] = useState<string | null>(null);

    useEffect(() => {
        const updateApiRole = () => {
            setApiRole(MeService.getApiRole());
        };

        // Check immediately
        updateApiRole();

        // Subscribe to changes
        const unsubscribe = MeService.subscribe(updateApiRole);

        // If not loaded yet, fetch it
        if (MeService.getMe() === null && !MeService.isLoading()) {
            MeService.fetchMe().catch(() => {
                // Error handling is done in MeService
            });
        }

        return unsubscribe;
    }, []);

    return apiRole;
};

